import { normalizeUnitFieldValue } from "@autodromo/units";
import { ContentFieldType } from "../../../types/cms/contentField.type";
import type { ChannelUnit } from "../../s3";
import { SINGLE_TOKEN_REGEX } from "../internal/constants";
import { transformRelationsToFlat } from "../internal/flatten";
import { resolveConditionals } from "../internal/resolveConditionals";
import type { InterpolateParams } from "../types";
import { buildReferenceLookups } from "./interpolateString";

/**
 * Unidade resolvida a partir de um campo `unit_id`, já com os derivados que o
 * lookup de referências monta (telefone principal, lista de telefones e
 * horários formatados).
 */
export type ResolvedUnit = ChannelUnit & {
  phoneNumber: string | null;
  phonesList: string;
  hoursFormatted: string;
};

/**
 * Resolve um template single-token `{{path|unit_id}}` para o objeto da unidade
 * do canal, em vez da representação textual que o `interpolateString` devolve.
 *
 * O campo guarda uma lista de ids, e registro salvo antes disso guarda uma
 * string solta. Vale o primeiro id que pertence ao canal.
 *
 * Combina com `infer` para descobrir o campo pelo tipo, sem template manual:
 * `interpolate({ structure, infer: { type: ContentFieldType.UNIT_ID, context: "item" } }).unit`
 */
export function extractUnit({
  structure,
  template,
}: InterpolateParams): ResolvedUnit | undefined {
  const match = resolveConditionals({ template, structure }).match(SINGLE_TOKEN_REGEX);
  if (!match) return undefined;

  const [, path, type] = match;
  if (type !== ContentFieldType.UNIT_ID) return undefined;

  const flatStructure = transformRelationsToFlat(structure);
  const unitMap = buildReferenceLookups({
    dynamicStructure: flatStructure,
  }).unit_id;

  let value: unknown = flatStructure;

  for (const key of path.split(".")) {
    if (value == null || typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[key];
  }

  for (const id of normalizeUnitFieldValue(value)) {
    const unit = unitMap.get(id);
    if (unit) return unit as ResolvedUnit;
  }

  return undefined;
}
